import {
  normalizeSelection,
  pickExistingId,
  type GameSelection,
  type SelectionState,
} from './SelectionState';

export type MenuView = 'start' | 'scores' | 'cars' | 'replay' | 'race';

export interface MenuFlowSnapshot {
  view: MenuView;
  selectedTrackId: number;
  selectedCarId: number;
}

export interface MenuAsyncToken {
  generation: number;
  view: MenuView;
  trackId: number | null;
}

export interface MenuFlowOptions {
  initialSelection: GameSelection;
  selectionState: SelectionState;
  trackIds: readonly number[];
  carIds: readonly number[];
}

export interface MenuFlowController {
  get(): MenuFlowSnapshot;
  selectTrack(trackId: number): MenuFlowSnapshot;
  selectCar(carId: number): MenuFlowSnapshot;
  openScores(): MenuFlowSnapshot;
  openCars(): MenuFlowSnapshot;
  openReplay(): MenuFlowSnapshot;
  startRace(): MenuFlowSnapshot;
  captureAsync(view: MenuView, trackId?: number): MenuAsyncToken;
  isCurrent(token: MenuAsyncToken): boolean;
  shutdown(): void;
  isActive(): boolean;
}

export function createMenuFlowController(options: MenuFlowOptions): MenuFlowController {
  const normalized = normalizeSelection(options.initialSelection, options.trackIds, options.carIds);
  let state: MenuFlowSnapshot = {
    view: 'start',
    selectedTrackId: normalized.trackId,
    selectedCarId: normalized.carId,
  };
  let generation = 0;
  let active = true;

  const navigate = (view: MenuView): MenuFlowSnapshot => {
    generation += 1;
    state = { ...state, view };
    return { ...state };
  };

  return {
    get(): MenuFlowSnapshot {
      return { ...state };
    },
    selectTrack(trackId: number): MenuFlowSnapshot {
      state = {
        ...state,
        selectedTrackId: pickExistingId(trackId, options.trackIds),
      };
      options.selectionState.set({
        carId: state.selectedCarId,
        trackId: state.selectedTrackId,
      });
      return navigate('scores');
    },
    selectCar(carId: number): MenuFlowSnapshot {
      state = {
        ...state,
        selectedCarId: pickExistingId(carId, options.carIds),
      };
      return navigate('cars');
    },
    openScores(): MenuFlowSnapshot {
      return navigate('scores');
    },
    openCars(): MenuFlowSnapshot {
      return navigate('cars');
    },
    openReplay(): MenuFlowSnapshot {
      return navigate('replay');
    },
    startRace(): MenuFlowSnapshot {
      options.selectionState.set({
        carId: state.selectedCarId,
        trackId: state.selectedTrackId,
      });
      return navigate('race');
    },
    captureAsync(view: MenuView, trackId: number | undefined = undefined): MenuAsyncToken {
      return { generation, view, trackId: trackId ?? null };
    },
    isCurrent(token: MenuAsyncToken): boolean {
      return (
        active &&
        token.generation === generation &&
        token.view === state.view &&
        (token.trackId === null || token.trackId === state.selectedTrackId)
      );
    },
    shutdown(): void {
      active = false;
      generation += 1;
    },
    isActive(): boolean {
      return active;
    },
  };
}
